/**
 * @file apps/web/src/pages/ApprovalsPage.tsx
 * @description Approval workflows for a project.
 * Lists approvals with status; clicking one shows ApprovalStepper + AuditTrail.
 * "New Approval" panel shows RoutingBuilder + entity selection form.
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { notifyProjectOverviewChanged } from '../lib/projectOverviewEvents.js';
import { useProjectRefresh } from '../lib/useProjectRefresh.js';
import { Plus, X, AlertCircle, CheckSquare, Clock, XCircle } from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { approvalsApi, projectsApi, testScriptsApi, uatApi, documentsApi } from '../lib/api.js';
import { ApprovalStepper, type ApprovalStep } from '../components/ApprovalStepper.js';
import { AuditTrail, type AuditEvent } from '../components/AuditTrail.js';
import { RoutingBuilder, type RoutingStep, type WorkflowType } from '../components/RoutingBuilder.js';
import { Badge } from '../components/Badge.js';
import { TemplateManagerPanel } from '../components/TemplateManagerPanel.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Approval {
  id:            string;
  status:        string;
  workflow_type: WorkflowType;
  entity_type:   string;
  entity_id:     string;
  created_at:    string;
  steps?:        ApprovalStep[];
}

interface TeamMember {
  id:        string;
  full_name?: string | null;
  email:     string;
}

interface EntityOption {
  id:    string;
  label: string;
}

// ---------------------------------------------------------------------------
// Approval list item
// ---------------------------------------------------------------------------

function ApprovalItem({
  approval,
  active,
  onClick,
}: {
  approval: Approval;
  active:   boolean;
  onClick:  () => void;
}) {
  const statusIcon = {
    pending:  <Clock size={13} className="text-muted" />,
    approved: <CheckSquare size={13} className="text-success" />,
    rejected: <XCircle size={13} className="text-danger" />,
    cancelled:<XCircle size={13} className="text-muted" />,
  }[approval.status] ?? <Clock size={13} className="text-muted" />;

  return (
    <button
      onClick={onClick}
      className={[
        'w-full text-left p-3 rounded-lg border transition-colors',
        active
          ? 'border-blue bg-sky'
          : 'border-[rgba(8,17,31,0.08)] hover:border-blue/30 hover:bg-sky/20',
      ].join(' ')}
    >
      <div className="flex items-start gap-2">
        <span className="mt-0.5">{statusIcon}</span>
        <div className="flex-1 min-w-0">
          <p className={['text-sm font-medium truncate', active ? 'text-blue' : 'text-ink'].join(' ')}>
            {approval.entity_type.replace('_', ' ')} approval
          </p>
          <p className="text-[11px] font-mono text-muted mt-0.5">
            {approval.workflow_type} · {new Date(approval.created_at).toLocaleDateString()}
          </p>
        </div>
        <Badge variant={approval.status as never} size="xs" />
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// New approval form
// ---------------------------------------------------------------------------

// Labels and API loaders for each entity type
const ENTITY_TYPES = [
  { value: 'project',     label: 'Project' },
  { value: 'test_script', label: 'Test Script' },
  { value: 'uat_session', label: 'UAT Session' },
  { value: 'document',    label: 'Document' },
  { value: 'sign_off',    label: 'Sign-off' },
] as const;

function entitySelectLabel(type: string): string {
  return {
    project:     'Select Project',
    test_script: 'Select Test Script',
    uat_session: 'Select Session',
    document:    'Select Document',
    sign_off:    'Select Sign-off',
  }[type] ?? 'Select Entity';
}

function NewApprovalPanel({
  teamMembers,
  projectId,
  onSubmit,
  onCancel,
}: {
  teamMembers: TeamMember[];
  projectId:   string;
  onSubmit:    (body: Record<string, unknown>) => Promise<void>;
  onCancel:    () => void;
}) {
  const { user, devHeaders } = useAuth();
  const [steps, setSteps]           = useState<RoutingStep[]>([]);
  const [wfType, setWfType]         = useState<WorkflowType>('sequential');
  const [entityType, setEntityType] = useState('project');
  const [entityId, setEntityId]     = useState('');
  const [entityOptions, setEntityOptions] = useState<EntityOption[]>([]);
  const [entityLoading, setEntityLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr]               = useState('');

  // Load entity options whenever entity type changes
  useEffect(() => {
    let cancelled = false;
    setEntityId('');
    setEntityOptions([]);
    setEntityLoading(true);

    const load = async (): Promise<EntityOption[]> => {
      if (entityType === 'project') {
        const data = await projectsApi.list(user.tenantId, devHeaders) as { id: string; name: string }[];
        return data.map((p) => ({ id: p.id, label: p.name }));
      }
      if (entityType === 'test_script') {
        const data = await testScriptsApi.list(projectId, devHeaders) as { id: string; title: string }[];
        return data.map((s) => ({ id: s.id, label: s.title }));
      }
      if (entityType === 'uat_session') {
        const data = await uatApi.listSessions(projectId, devHeaders) as { id: string; name: string }[];
        return data.map((s) => ({ id: s.id, label: s.name }));
      }
      if (entityType === 'document') {
        const data = await documentsApi.list(projectId, devHeaders) as { id: string; title: string }[];
        return data.map((d) => ({ id: d.id, label: d.title }));
      }
      return [];
    };

    load()
      .then((opts) => { if (!cancelled) { setEntityOptions(opts); setEntityLoading(false); } })
      .catch(() => { if (!cancelled) setEntityLoading(false); });

    return () => { cancelled = true; };
  }, [entityType, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (steps.length === 0) { setErr('Add at least one approver.'); return; }
    if (!entityId)          { setErr('Please select an entity.'); return; }
    setSubmitting(true);
    setErr('');
    try {
      await onSubmit({
        entity_type:   entityType,
        entity_id:     entityId,
        workflow_type: wfType,
        steps: steps.map((s, i) => ({
          approver_id:    s.approverId,
          step_order:     i + 1,
          parallel_group: s.parallelGroup,
          condition_expr: s.conditionExpr ?? null,
          is_required:    s.isRequired,
        })),
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to create approval');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card p-5 space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-sm font-semibold text-ink">New Approval Workflow</h3>
        <button onClick={onCancel} className="btn-icon text-muted hover:text-ink">
          <X size={16} />
        </button>
      </div>

      {/* Entity */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label mb-1.5">Entity Type</label>
          <select
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            className="select text-sm"
          >
            {ENTITY_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label mb-1.5">{entitySelectLabel(entityType)}</label>
          <select
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            className="select text-sm"
            disabled={entityLoading}
          >
            <option value="">
              {entityLoading ? 'Loading…' : `— choose ${ENTITY_TYPES.find((t) => t.value === entityType)?.label ?? 'entity'} —`}
            </option>
            {entityOptions.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Routing builder */}
      <RoutingBuilder
        teamMembers={teamMembers}
        value={steps}
        workflowType={wfType}
        onChange={(s, t) => { setSteps(s); setWfType(t); }}
      />

      {err && (
        <div className="flex items-center gap-2 text-sm text-danger">
          <AlertCircle size={13} /> {err}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-[rgba(8,17,31,0.06)]">
        <button onClick={onCancel} className="btn-ghost text-sm">Cancel</button>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="btn-primary text-sm"
        >
          {submitting ? 'Creating…' : 'Create Workflow'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ApprovalsPage
// ---------------------------------------------------------------------------

export function ApprovalsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { user, devHeaders } = useAuth();

  const [approvals, setApprovals]     = useState<Approval[]>([]);
  const [activeId, setActiveId]       = useState<string | null>(null);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [showNew, setShowNew]         = useState(false);
  const [tab, setTab] = useState<'active' | 'setup'>('active');
  const [currentProjectRole, setCurrentProjectRole] = useState<string | undefined>(undefined);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [apprs, members] = await Promise.all([
        approvalsApi.list(projectId, devHeaders) as Promise<Approval[]>,
        projectsApi.members(projectId, devHeaders) as Promise<TeamMember[]>, // project members only: POST /approvals rejects anyone else
      ]);
      setApprovals(apprs);
      setTeamMembers(members);
      if (apprs.length > 0 && !activeId) setActiveId(apprs[0]!.id);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // The client decides in the portal window; bring this window up to date when it is shown again.
  useProjectRefresh(projectId, () => { void load(); });

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    projectsApi.members(projectId, devHeaders)
      .then((data) => {
        if (cancelled) return;
        const members = data as { id: string; role: string }[];
        setCurrentProjectRole(members.find((m) => m.id === user.userId)?.role);
      })
      .catch(() => { if (!cancelled) setCurrentProjectRole(undefined); });
    return () => { cancelled = true; };
  }, [projectId, devHeaders, user.userId]);

  const isAdmin = currentProjectRole === 'lead' || user.tenantRole === 'owner' || user.tenantRole === 'admin';

  // Load audit log when active approval changes
  useEffect(() => {
    if (!activeId) return;
    let cancelled = false;
    setAuditLoading(true);
    approvalsApi.getAudit(activeId, devHeaders)
      .then((data) => { if (!cancelled) setAuditEvents(data as AuditEvent[]); })
      .catch(() => { if (!cancelled) setAuditEvents([]); })
      .finally(() => { if (!cancelled) setAuditLoading(false); });
    return () => { cancelled = true; };
  }, [activeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Create new approval
  const handleCreate = async (body: Record<string, unknown>) => {
    const created = await approvalsApi.create({
      ...body,
      project_id:  projectId,
      requested_by: user.userId,
    }, devHeaders) as Approval;
    setApprovals((prev) => [created, ...prev]);
    setActiveId(created.id);
    setShowNew(false);
    if (projectId) notifyProjectOverviewChanged(projectId);
  };

  // Make approval decision
  const handleDecide = async (stepId: string, decision: 'approved' | 'rejected') => {
    if (!activeId) return;
    await approvalsApi.decide(activeId, stepId, { decision }, devHeaders);
    // Reload to get fresh step states + audit
    const updated = await approvalsApi.get(activeId, devHeaders) as Approval;
    setApprovals((prev) => prev.map((a) => a.id === activeId ? updated : a));
    const events = await approvalsApi.getAudit(activeId, devHeaders) as AuditEvent[];
    setAuditEvents(events);
    // A final decision moves the document and its design item server-side.
    if (projectId) notifyProjectOverviewChanged(projectId);
  };

  const activeApproval = approvals.find((a) => a.id === activeId);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 p-0.5 rounded-lg bg-gray/60 w-fit">
          <button
            onClick={() => setTab('active')}
            className={[
              'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
              tab === 'active' ? 'bg-white text-ink shadow-sm' : 'text-muted hover:text-ink',
            ].join(' ')}
          >
            Active
          </button>
          <button
            onClick={() => setTab('setup')}
            className={[
              'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
              tab === 'setup' ? 'bg-white text-ink shadow-sm' : 'text-muted hover:text-ink',
            ].join(' ')}
          >
            Setup
          </button>
        </div>
        {tab === 'active' && !showNew && (
          <button onClick={() => setShowNew(true)} className="btn-primary text-sm">
            <Plus size={14} /> New Approval
          </button>
        )}
      </div>

      {tab === 'active' && (
        <>
          {error && (
            <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm">
              <AlertCircle size={14} className="shrink-0" /> {error}
            </div>
          )}

          {/* New approval panel */}
          {showNew && (
            <NewApprovalPanel
              teamMembers={teamMembers}
              projectId={projectId ?? ''}
              onSubmit={handleCreate}
              onCancel={() => setShowNew(false)}
            />
          )}

          {loading ? (
            <div className="grid grid-cols-[200px_1fr] gap-4">
              <div className="space-y-2 animate-pulse">
                {[1,2,3].map((i) => <div key={i} className="card h-16 bg-gray/60" />)}
              </div>
              <div className="card h-64 animate-pulse bg-gray/60" />
            </div>
          ) : approvals.length === 0 && !showNew ? (
            <div className="empty-state py-16">
              <CheckSquare size={32} className="text-muted/30 mb-3 mx-auto" />
              <p className="text-sm text-muted">No approval workflows yet.</p>
              <button onClick={() => setShowNew(true)} className="btn-secondary mt-4">
                <Plus size={14} /> Create First Workflow
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-[220px_1fr] gap-4 items-start">
              {/* Approval list */}
              <aside className="space-y-2">
                {approvals.map((a) => (
                  <ApprovalItem
                    key={a.id}
                    approval={a}
                    active={a.id === activeId}
                    onClick={() => { setActiveId(a.id); setShowNew(false); }}
                  />
                ))}
              </aside>

              {/* Detail panel */}
              {activeApproval && (
                <div className="space-y-4">
                  {/* Stepper */}
                  <div className="card p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-display text-sm font-semibold text-ink">
                        Approval Chain
                      </h3>
                      <div className="flex items-center gap-2">
                        <Badge variant={activeApproval.status as never} />
                        <span className="text-xs font-mono text-muted">{activeApproval.workflow_type}</span>
                      </div>
                    </div>
                    <ApprovalStepper
                      steps={activeApproval.steps ?? []}
                      workflowType={activeApproval.workflow_type}
                      currentUserId={user.userId}
                      onDecide={handleDecide}
                    />
                  </div>

                  {/* Audit trail */}
                  <div className="card p-5">
                    <h3 className="font-display text-sm font-semibold text-ink mb-4">
                      Audit Trail
                    </h3>
                    <AuditTrail events={auditEvents} loading={auditLoading} />
                  </div>

                  {/* Cancel button for pending approvals */}
                  {!['approved', 'rejected', 'cancelled'].includes(activeApproval.status) && (
                    <div className="flex justify-end">
                      <button
                        onClick={async () => {
                          const reason = prompt('Reason for cancellation (optional):') ?? '';
                          await approvalsApi.cancel(activeApproval.id, reason, devHeaders);
                          const updated = await approvalsApi.get(activeApproval.id, devHeaders) as Approval;
                          setApprovals((prev) => prev.map((a) => a.id === activeApproval.id ? updated : a));
                          if (projectId) notifyProjectOverviewChanged(projectId);
                        }}
                        className="btn-ghost text-sm text-danger hover:bg-danger/8"
                      >
                        <XCircle size={14} /> Cancel Workflow
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {tab === 'setup' && (
        <TemplateManagerPanel
          projectId={projectId ?? ''}
          teamMembers={teamMembers}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
}
